import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { httpClient } from '../../utils/apiCall';
import { API_ENDPOINTS } from '../../config/api';
import {
  type ApiQuote,
  ALL_COLUMNS,
  INITIALLY_HIDDEN,
  SupplierList,
  fmtDate,
  fmtNum,
} from './quoteRoundsView';
import {
  ExchangeRatePrompt,
  ManualRateNote,
  needsRatePrompt,
  type ExchangeRateInfo,
  type ManualRates,
} from './ExchangeRatePrompt';

// ─── Backend response tipleri ────────────────────────────────────────────────

interface ApiItem {
  id: number;
  itemCode: string;
  itemName?: string | null;
  unit: string;
}

interface ApiPORequestBlock {
  id: number;
  createdAt: string;
  createdByName?: string | null;
  requestedQuantity: number;
  requestedDeliveryDate?: string | null;
  requestStatus: string;
  suppliers: ApiQuote[];
}

interface ApiReport {
  item: ApiItem;
  poRequests: ApiPORequestBlock[];
  exchangeRates?: ExchangeRateInfo;
}

// RequestInfo.tsx ile aynı status haritası — küçük harfe çevirip eşleştirir.
function statusMeta(status: string): { label: string; tone: 'gray' | 'amber' | 'red' | 'blue' | 'indigo' | 'green' } {
  const s = (status || '').trim().toLowerCase();
  switch (s) {
    case 'draft':            return { label: 'Taslak',                  tone: 'gray'   };
    case 'pending':          return { label: 'Yönetici Onayında',       tone: 'amber'  };
    case 'rejected':         return { label: 'Reddedildi',              tone: 'red'    };
    case 'approved':         return { label: 'Teklif Bekleniyor',       tone: 'blue'   };
    case 'convertedtoquote': return { label: 'Teklif Gönderildi',       tone: 'blue'   };
    case 'readytoevaluate':  return { label: 'Değerlendirmede',         tone: 'indigo' };
    case 'convertedtopo':    return { label: 'Sipariş Oluşturuldu',     tone: 'green'  };
    default:                 return { label: status || '—',             tone: 'gray'   };
  }
}

const STATUS_TONE_CLASSES: Record<ReturnType<typeof statusMeta>['tone'], string> = {
  gray:   'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  amber:  'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  red:    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  blue:   'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  green:  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
};

const ModalInner: React.FC<{ itemId: number; onClose: () => void }> = ({ itemId, onClose }) => {
  const [report, setReport] = useState<ApiReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => new Set(INITIALLY_HIDDEN));
  const [chooserOpen, setChooserOpen] = useState(false);
  const chooserRef = useRef<HTMLDivElement>(null);
  // Netsis'te bugünün kuru yoksa kullanıcının bu açılış için girdiği kurlar (kaydedilmez)
  const [manualRates, setManualRates] = useState<ManualRates>({});
  const [ratePromptInfo, setRatePromptInfo] = useState<ExchangeRateInfo | null>(null);

  useEffect(() => {
    if (!chooserOpen) return;
    const onClick = (e: MouseEvent) => {
      if (chooserRef.current && !chooserRef.current.contains(e.target as Node)) {
        setChooserOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [chooserOpen]);

  const toggleColumn = (colId: string) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId);
      else next.add(colId);
      return next;
    });
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    httpClient
      .get<ApiReport>(API_ENDPOINTS.ITEM.QUOTE_REPORT(itemId), { params: manualRates })
      .then(r => {
        if (cancelled) return;
        if (r.success && r.data) {
          setReport(r.data);
          if (Object.keys(manualRates).length === 0 && needsRatePrompt(r.data.exchangeRates)) {
            setRatePromptInfo(r.data.exchangeRates!);
          }
        } else setError(r.message || 'Rapor yüklenemedi');
      })
      .catch(e => {
        if (!cancelled) setError(e?.message || 'Rapor yüklenemedi');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [itemId, manualRates]);

  const item = report?.item;
  const poRequests = report?.poRequests ?? [];

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-7xl flex flex-col border border-gray-200 dark:border-gray-700"
        style={{ height: '85vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Başlık */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4 flex-shrink-0">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate flex items-center gap-2">
              {item?.itemCode && (
                <span className="text-gray-500 dark:text-gray-400 font-normal">{item.itemCode}</span>
              )}
              <span>· {item?.itemName ?? '—'}</span>
            </p>
            <div className="flex flex-wrap gap-x-4 text-xs text-gray-900 dark:text-white mt-0.5">
              <span>
                <span className="text-gray-500 dark:text-gray-400">Birim: </span>
                {item?.unit ?? '—'}
              </span>
              <span>
                <span className="text-gray-500 dark:text-gray-400">Talep Sayısı: </span>
                {poRequests.length}
              </span>
            </div>
            <ManualRateNote info={report?.exchangeRates} className="mt-1 inline-block" />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="relative" ref={chooserRef}>
              <button
                type="button"
                onClick={() => setChooserOpen(o => !o)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                aria-label="Kolonlar"
              >
                <i className="fas fa-columns text-[10px]" />
                Kolonlar
              </button>
              {chooserOpen && (
                <div className="absolute right-0 mt-1 z-30 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-lg py-1 text-xs max-h-72 overflow-auto">
                  {ALL_COLUMNS.map(c => (
                    <label
                      key={c.colId}
                      className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/60 text-gray-700 dark:text-gray-200"
                    >
                      <input
                        type="checkbox"
                        checked={!hiddenColumns.has(c.colId)}
                        onChange={() => toggleColumn(c.colId)}
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors text-sm pt-0.5 pl-1"
              aria-label="Kapat"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Talep blokları */}
        <div className="flex-1 min-h-0 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center w-full h-full text-sm text-gray-500 dark:text-gray-400">
              Yükleniyor…
            </div>
          ) : error ? (
            <div className="flex items-center justify-center w-full h-full text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          ) : poRequests.length === 0 ? (
            <div className="flex items-center justify-center w-full h-full text-sm text-gray-500 dark:text-gray-400">
              Bu ürün için henüz talep oluşturulmamış.
            </div>
          ) : (
            <div className="px-4 py-3 space-y-4">
              {poRequests.map(req => (
                <section
                  key={req.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden"
                >
                  {/* Talep başlığı */}
                  <header className="bg-gray-50 dark:bg-gray-800/80 px-4 py-2.5 flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      Talep #{req.id}
                    </span>
                    <span className="text-xs text-gray-600 dark:text-gray-300">
                      <span className="text-gray-500 dark:text-gray-400">Açılış: </span>
                      {fmtDate(req.createdAt, false)}
                    </span>
                    {req.createdByName && (
                      <span className="text-xs text-gray-600 dark:text-gray-300">
                        <span className="text-gray-500 dark:text-gray-400">Açan: </span>
                        {req.createdByName}
                      </span>
                    )}
                    <span className="text-xs text-gray-600 dark:text-gray-300">
                      <span className="text-gray-500 dark:text-gray-400">İstenen Miktar: </span>
                      {fmtNum(req.requestedQuantity)} {item?.unit ?? ''}
                    </span>
                    <span className="text-xs text-gray-600 dark:text-gray-300">
                      <span className="text-gray-500 dark:text-gray-400">İstenen Teslim: </span>
                      {req.requestedDeliveryDate ? fmtDate(req.requestedDeliveryDate, false) : '—'}
                    </span>
                    {(() => {
                      const meta = statusMeta(req.requestStatus);
                      return (
                        <span className={`ml-auto text-[11px] px-2 py-0.5 rounded ${STATUS_TONE_CLASSES[meta.tone]}`}>
                          {meta.label}
                        </span>
                      );
                    })()}
                  </header>

                  <SupplierList
                    quotes={req.suppliers}
                    hiddenColumns={hiddenColumns}
                    gridStateKey={`item-quote-report-${req.id}`}
                  />
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
      {ratePromptInfo && (
        <ExchangeRatePrompt
          info={ratePromptInfo}
          onSubmit={rates => {
            setRatePromptInfo(null);
            setManualRates(rates);
          }}
          onSkip={() => setRatePromptInfo(null)}
        />
      )}
    </div>
  );
};

const ItemQuoteReportModal: React.FC<{ itemId: number; onClose: () => void }> = props =>
  ReactDOM.createPortal(<ModalInner {...props} />, document.body);

export default ItemQuoteReportModal;
